import Box from "@mui/material/Box";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Stepper from "@mui/material/Stepper";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useEventSession } from "../../providers/eventSessions";
import { IEventSession } from "../../providers/eventSessions/eventsSessions.interface";
import EventSessionIntegrationForm from "../EventSessionIntegrationForm";
import EventSessionNotificationForm from "../EventSessionNotification";
import EventSessionReplayForm from "../EventSessionReplayForm";
import EventSessionScheduleForm from "../EventSessionScheduleForm";
import EventSessionUpdateLiveForm from "../EventSessionUpdateLive";
import EventSessionsUpdateSettings from "../EventSessionsUpdateSettings";
import WidgetSettingsForm from "../EventSessionWidgetForm";
import EventSessionStyles from "../EventSessionStyles";

export default function EventSessionUpdateStepper() {
  const [activeStep, setActiveStep] = React.useState(0);
  const [eventSession, setEventSession] = React.useState<IEventSession | null>(
    null
  );
  const [completed, setCompleted] = React.useState<{
    [k: number]: boolean;
  }>({});
  const navigate = useNavigate();
  const { session_id, path } = useParams();
  const { allEventSessions, getEventSessionById } = useEventSession();
  const [stepsLocked, setStepsLocked] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (allEventSessions) {
      const eventFromId = allEventSessions.find(
        (e: IEventSession) => e.id === session_id
      );
      if (eventFromId) {
        setEventSession(eventFromId);
        // Update completed state based on eventSession properties
        setCompleted({
          0: true, // Always true for settings
          1: !!eventFromId.sessionSchedule,
          2: !!eventFromId.integrations,
          3: !!eventFromId.whatsappNotifications,
          4: !!eventFromId.widget,
          5: !!eventFromId.waitingRoom,
          6: !!eventFromId.actionText,
          7: !!eventFromId.replayControls,
        });

        // Set locked state based on condition
        setStepsLocked(eventFromId && !eventFromId.liveContents);
      } else {
        navigate("/" + path + "/events");
      }
    }
  }, [allEventSessions, navigate, path, session_id]);

  React.useEffect(() => {
    const fetchEventSession = async () => {
      if (eventSession && !eventSession.liveContents) {
        try {
          const newSession = await getEventSessionById(eventSession.id);
          setEventSession(newSession);
          // Update locked state after fetching
          setStepsLocked(newSession && !newSession.liveContents);
        } catch (error) {
          console.error("Error fetching eventSession:", error);
        }
      }
    };

    fetchEventSession();
  }, [eventSession, getEventSessionById]);

  const { t } = useTranslation();

  const totalSteps = () => {
    return steps.length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  const handleNext = () => {
    // If steps are locked and not in settings, prevent navigation
    if (stepsLocked && activeStep !== 0) {
      return;
    }

    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step: number) => () => {
    // Only allow clicking on settings (step 0) if steps are locked
    if (stepsLocked && step !== 0) {
      return;
    }
    setActiveStep(step);
  };

  const handleComplete = () => {
    // If we're in settings, we might update the lock status
    if (activeStep === 0) {
      // Check if the liveContents condition has changed
      setStepsLocked(eventSession && !eventSession.liveContents);
    }

    // If steps are locked and not in settings, prevent completion
    if (stepsLocked && activeStep !== 0) {
      return;
    }

    const newCompleted = completed;
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    handleNext();
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompleted({});
  };

  const steps = [
    {
      name: t("settings"),
      component: (
        <EventSessionsUpdateSettings
          key={"settingsUpdate"}
          eventSession={eventSession}
          setEventSession={setEventSession}
          handleComplete={handleComplete}
        />
      ),
    },
    {
      name: t("schedules"),
      component: (
        <EventSessionScheduleForm
          key={"settingsCreation"}
          eventSession={eventSession}
          setEventSession={setEventSession}
          handleComplete={handleComplete}
          handleBack={handleBack}
        />
      ),
    },
    {
      name: "Integrations",
      component: (
        <EventSessionIntegrationForm
          key={"settingsCreation"}
          eventSession={eventSession}
          setEventSession={setEventSession}
          handleComplete={handleComplete}
          handleBack={handleBack}
        />
      ),
    },
    {
      name: t("notifications"),
      component: (
        <EventSessionNotificationForm
          key={"settingsLive"}
          eventSession={eventSession}
          setEventSession={setEventSession}
          handleComplete={handleComplete}
          handleBack={handleBack}
        />
      ),
    },
    ...(eventSession?.sessionNumber === 1
      ? [
          {
            name: "Widget",
            component: (
              <WidgetSettingsForm
                key={"widgetStepper"}
                eventSession={eventSession}
                setEventSession={setEventSession}
                handleComplete={handleComplete}
                handleBack={handleBack}
              />
            ),
          },
        ]
      : []),
    {
      name: t("styles"),
      component: (
        <EventSessionStyles
          key={"settingsStyles"}
          eventSession={eventSession}
          setEventSession={setEventSession}
          handleComplete={handleComplete}
          handleBack={handleBack}
        />
      ),
    },
    {
      name: t("live"),
      component: (
        <EventSessionUpdateLiveForm
          key={"settingsLive"}
          eventSession={eventSession}
          setEventSession={setEventSession}
          handleComplete={handleComplete}
          handleBack={handleBack}
        />
      ),
    },
    {
      name: "Replay",
      component: (
        <EventSessionReplayForm
          key={"settingsLive"}
          eventSession={eventSession}
          setEventSession={setEventSession}
          handleComplete={handleComplete}
          handleBack={handleBack}
        />
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Stepper alternativeLabel nonLinear activeStep={activeStep}>
        {steps.map((label, index) => (
          <Step
            key={label.name + index}
            completed={completed[index]}
            disabled={stepsLocked && index !== 0} // Disable all steps except settings when locked
          >
            <StepButton color="inherit" onClick={handleStep(index)}>
              {label.name}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      <div>
        <React.Fragment>
          {stepsLocked && activeStep !== 0 ? (
            <Box sx={{ mt: 4, p: 3, textAlign: "center" }}>
              {t("Please complete the settings section first")}
            </Box>
          ) : (
            steps[activeStep].component
          )}
        </React.Fragment>
      </div>
    </Box>
  );
}
